import { Controller } from '@hotwired/stimulus'
import TomSelect from 'tom-select'

export type TomSelectable = (HTMLInputElement | HTMLSelectElement) & {
  tomselect?: TomSelect
}

export type TomSelectOptions = ConstructorParameters<typeof TomSelect>[1]

export default class TomSelectController extends Controller<TomSelectable> {
  // Lifecycle methods
  connect() {
    new TomSelect(this.element, this.getOptions())
  }

  disconnect() {
    this.element.tomselect?.destroy()
  }

  // Protected methods

  protected additionalOptions(): Partial<TomSelectOptions> {
    return {}
  }

  protected getOptions() {
    const { tomSelectOptions } = this.element.dataset
    const customOptions = JSON.parse(tomSelectOptions || '{}')

    return {
      ...this.#baseOptions(),
      ...this.additionalOptions(),
      ...customOptions,
    } satisfies TomSelectOptions
  }

  // Private methods

  #baseOptions() {
    return { ...this.#callbackOptions() }
  }

  /**
   * @docs https://tom-select.js.org/docs/#callbacks
   */
  #callbackOptions() {
    return {
      onBlur: () => {
        this.dispatch('blur')
      },
      onChange: (value: string) => {
        this.dispatch('change', { detail: { value } })
      },
      onClear: () => {
        this.dispatch('clear')
      },
      onDelete: (values: string[], event: Event) => {
        this.dispatch('delete', { detail: { values, event } })
      },
      onDropdownClose: (dropdown: any) => {
        this.dispatch('dropdownClose', { detail: { dropdown } })
      },
      onDropdownOpen: (dropdown: any) => {
        this.dispatch('dropdownOpen', { detail: { dropdown } })
      },
      onFocus: () => {
        this.dispatch('focus')
      },
      onInitialize: () => {
        this.dispatch('initialize')
      },
      onItemAdd: (value: string, $item: HTMLElement) => {
        this.dispatch('itemAdd', { detail: { value, $item } })
      },
      onItemRemove: (value: string, $item: HTMLElement) => {
        this.dispatch('itemRemove', { detail: { value, $item } })
      },
      onLoad: (options: any, optgroup: any) => {
        this.dispatch('load', { detail: { options, optgroup } })
      },
      onOptionAdd: (value: string, data: any) => {
        this.dispatch('optionAdd', { detail: { value, data } })
      },
      onOptionRemove: (value: string) => {
        this.dispatch('optionRemove', { detail: { value } })
      },
      onType: (str: string) => {
        this.dispatch('type', { detail: { str } })
      },
    } satisfies TomSelectOptions
  }
}
